import { cn } from '../../lib/cn';
import { clamp01, formatExact, formatPercent } from '../../lib/format';
import { ProgressBar } from '../primitives/ProgressBar';
import { Tooltip } from '../primitives/Tooltip';
import { RankIcon } from '../../icons/ui-icons';

export interface AccountLevelProps {
  level: number;
  /** XP earned toward the next level. */
  xp: number;
  /** XP required for the next level (polynomial progression, GDD §2). */
  xpForNext: number;
  /** Unspent talent points — one is granted per level-up. */
  talentPoints?: number;
  /** `compact` for the HUD strip, `full` for the profile panel. */
  variant?: 'compact' | 'full';
  onOpenTalents?: () => void;
  className?: string;
}

/**
 * Commander rank and XP readout (GDD §2 account progression).
 *
 * Unspent talent points get a pulsing magenta marker because they are pure
 * un-collected value — a player sitting on points is strictly weaker than one
 * who spent them, and the HUD should say so.
 */
export function AccountLevel({
  level,
  xp,
  xpForNext,
  talentPoints = 0,
  variant = 'compact',
  onOpenTalents,
  className,
}: AccountLevelProps) {
  const ratio = clamp01(xpForNext > 0 ? xp / xpForNext : 0);

  const tooltip = (
    <div className="space-y-0.5">
      <div className="font-display text-label uppercase text-content">Account-Level {level}</div>
      <div data-sg-numeric className="text-content-secondary">
        {formatExact(xp)} / {formatExact(xpForNext)} XP
      </div>
      {talentPoints > 0 ? (
        <div className="text-chronos">
          {talentPoints} Talentpunkt{talentPoints === 1 ? '' : 'e'} verfügbar
        </div>
      ) : null}
    </div>
  );

  if (variant === 'compact') {
    return (
      <Tooltip content={tooltip} side="bottom">
        <button
          type="button"
          onClick={onOpenTalents}
          className={cn(
            'flex items-center gap-2 rounded-control px-2 py-1 text-left',
            'transition-colors duration-[var(--sg-duration-fast)] hover:bg-surface-3',
            className,
          )}
        >
          <span className="relative">
            <RankIcon size={18} className="text-silicate" />
            {talentPoints > 0 ? (
              <span
                aria-hidden
                className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse-glow rounded-full bg-chronos shadow-glow-chronos"
              />
            ) : null}
          </span>

          <span className="flex min-w-0 flex-col leading-none">
            <span className="font-display text-label-sm uppercase text-content-muted">
              Level <span data-sg-numeric>{level}</span>
            </span>
            <span className="mt-1 block h-1 w-16 overflow-hidden rounded-pill bg-surface-base">
              <span
                className="block h-full rounded-pill bg-silicate"
                style={{ width: formatPercent(ratio) }}
              />
            </span>
          </span>
        </button>
      </Tooltip>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-12 w-12 shrink-0 items-center justify-center rounded-panel',
            'border border-silicate/40 bg-silicate/10 shadow-glow-sm',
          )}
        >
          <span data-sg-numeric className="text-readout font-bold text-silicate-bright">
            {level}
          </span>
        </div>

        <div className="min-w-0 flex-1">
          <div className="font-display text-label uppercase text-content-muted">
            Account-Level
          </div>
          <ProgressBar
            value={ratio}
            accent="silicate"
            size="sm"
            className="mt-1"
            hint={`${formatExact(xp)} / ${formatExact(xpForNext)} XP`}
            aria-label="Erfahrungsfortschritt"
          />
        </div>
      </div>

      {talentPoints > 0 ? (
        <button
          type="button"
          onClick={onOpenTalents}
          className={cn(
            'flex w-full items-center justify-between rounded-control border border-chronos/40',
            'bg-chronos/10 px-3 py-2 text-left transition-colors hover:bg-chronos/20',
          )}
        >
          <span className="font-display text-label uppercase text-chronos-bright">
            {talentPoints} Talentpunkt{talentPoints === 1 ? '' : 'e'} verfügbar
          </span>
          <span className="text-label uppercase text-chronos">Verteilen →</span>
        </button>
      ) : null}
    </div>
  );
}
